/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { AntFlowJob } from '@/types/models';
import { DateTime, Interval } from 'luxon';
import { plurals } from 'ogc-processes-components';
import { useTranslation } from 'react-i18next';
import { DataItem, TimelineItem, TimelineOptions } from 'vis-timeline';
import { DataGroupExtended } from '../types';
import { useMemo } from 'react';

type TimelineOptionsDataType = {
  job: AntFlowJob;
  offsetMinute: number;
};

export const useTimelineOptions = ({ job, offsetMinute }: TimelineOptionsDataType) => {
  const { t, i18n } = useTranslation();

  // Timeline Options
  const timelineOptions: TimelineOptions = useMemo(() => {
    return {
      start: DateTime.fromISO(job.created).plus({ minute: offsetMinute }).toJSDate(),
      zoomMin: 1000 * 10, // 1 second
      zoomMax: 1000 * 60 * 60 * 24, // 1 day
      orientation: 'top',
      min: DateTime.fromISO(job.created).plus({ minute: offsetMinute }).toJSDate(),
      align: 'auto',
      locale: i18n.language,
      verticalScroll: true,
      horizontalScroll: true,
      showCurrentTime: false,
      showTooltips: true,
      minHeight: '250px',
      width: '100%',
      margin: {
        item: 10,
        axis: 5
      },
      tooltip: {
        followMouse: true,
        overflowMethod: 'cap',
        template: (item: TimelineItem) => {
          const startTime = item.start
            ? DateTime.fromJSDate(item.start as Date)
                .setLocale(i18n.language)
                .toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)
            : 'N/A';

          const endTime = item.end
            ? DateTime.fromJSDate(item.end as Date)
                .setLocale(i18n.language)
                .toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)
            : t(['misc.ongoing', 'Ongoing']);

          let message = undefined;
          if (Object.hasOwn(item, 'message')) {
            message = (item['message' as never] as string) ?? '';
          }
          let duration = undefined;
          if (item.start && item.end) {
            const started = DateTime.fromJSDate(item.start as Date);
            const finished = DateTime.fromJSDate(item.end as Date);
            const interval = Interval.fromDateTimes(started, finished);
            duration =
              interval.length('seconds') <= 60
                ? `~ ${plurals(Math.ceil(interval.length('seconds')), 'sec')}.`
                : interval.length('minutes') <= 60
                  ? `~ ${plurals(Math.ceil(interval.length('minutes')), 'min')}.`
                  : ` ~ ${Math.ceil(interval.length('hours'))} h`;
          }

          if (item.id === 'item-start') {
            return `<div><code class="underline">${t(['misc.start_processing', 'Start'])}</code></div>`;
          }
          return `
                <div style="">
                  <div>
                    <code style="font-weight: bold; margin-bottom: 4px;">${item.content}</code>
                  </div>
                  <div>
                    <code style="font-size: 12px; color: #666; margin-bottom: 2px;">
                      <strong>${t(['misc.start', 'Start'])}:</strong> ${startTime}
                    </code>
                  </div>
                  <div>
                    <code style="font-size: 12px; color: #666; margin-bottom: 2px;">
                      <strong>${t(['misc.end', 'End'])}:</strong> ${endTime}
                    </code>
                  </div>
                  ${
                    duration
                      ? `
                      <div>
                    <code style="font-size: 12px; color: #666; margin-bottom: 2px;">
                      <strong>${t(['misc.duration', 'duration'])}:</strong> ${duration}
                    </code>
                  </div>
                  `
                      : ''
                  }
                  <div>
                      <code>
                       <strong>Status</strong>: ${t([`jobs.properties.status.${item.title}`, ''])}
                      </code>
                  </div>
                  ${
                    message
                      ? `
                      <code>
                      <strong>Message</strong> : <code><small>${message}</small></code>
                     </code>
                    `
                      : ''
                  }
                </div>
              `;
        }
      },
      groupOrder: (a: DataGroupExtended, b: DataGroupExtended) => (a.order ?? Infinity) - (b.order ?? Infinity),
      template: (item: DataItem, element: HTMLElement) => {
        let nodeAnchor = `<div><span class="font-semibold"></span></div>`;
        if (item.className?.includes('timeline-item-running')) {
          nodeAnchor = `
              <div class="flex align-items-center" style="min-width:50px;">
                <div class="mr-2 spinner-loader"></div>
                <div></div>
              </div>`;
        }
        element.innerHTML = nodeAnchor;
        return element.innerHTML;
      }
    };
  }, [job.created, t, i18n.language, offsetMinute]);

  return { timelineOptions };
};
